/**
 * AccountSearchModal-keyboard — keyboard handlers extracted to keep the
 * component file under the lint cap.
 */

import React from 'react'

import { type AccountSearchModalState } from './useAccountSearchModal'

import type { Account } from '../../services/account-service'

export interface KeyboardArgs {
  state: AccountSearchModalState
  shouldShowPartyTab: boolean
  onSelect: (account: Account) => void
  onClose: () => void
}

export function handleAccountKey(e: React.KeyboardEvent, args: KeyboardArgs): void {
  const { state, onSelect, onClose } = args
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    state.setSelectedIndex(prev => Math.min(prev + 1, state.displayAccounts.length - 1))
    return
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault()
    state.setSelectedIndex(prev => Math.max(prev - 1, 0))
    return
  }
  if (e.key === 'Enter') {
    const account = state.displayAccounts[state.selectedIndex]
    if (account) {
      e.preventDefault()
      onSelect(account)
      onClose()
    }
  }
}

export function handlePartyStep1Key(e: React.KeyboardEvent, args: KeyboardArgs): void {
  const { state } = args
  const lastIndex = state.parties.length // virtual "+ New party" row sits here
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    state.setPartySelectedIndex(prev => Math.min(prev + 1, lastIndex))
    return
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault()
    state.setPartySelectedIndex(prev => Math.max(prev - 1, 0))
    return
  }
  if (e.key !== 'Enter') { return }
  if (state.partySelectedIndex === lastIndex) {
    e.preventDefault()
    state.setIsAddPartyOpen(true)
    return
  }
  const party = state.parties[state.partySelectedIndex]
  if (party) {
    e.preventDefault()
    state.pickParty(party)
  }
}

export function handlePartyStep2Key(e: React.KeyboardEvent, args: KeyboardArgs): void {
  const { state } = args
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    state.setRoleSelectedIndex(prev => Math.min(prev + 1, state.roleAccounts.length - 1))
    return
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault()
    state.setRoleSelectedIndex(prev => Math.max(prev - 1, 0))
    return
  }
  if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
    e.preventDefault()
    state.goBackToPartySearch()
    return
  }
  if (e.key === 'Enter') {
    const roleAccount = state.roleAccounts[state.roleSelectedIndex]
    if (roleAccount) {
      e.preventDefault()
      void state.selectRoleAccount(roleAccount, state.roleSelectedIndex)
    }
  }
}

function isTabSwitchShortcut(e: React.KeyboardEvent, args: KeyboardArgs): boolean {
  return args.shouldShowPartyTab && (e.ctrlKey || e.metaKey)
    && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
}

export function handleKeyDown(e: React.KeyboardEvent, args: KeyboardArgs): void {
  const { state, onClose } = args
  e.stopPropagation()
  if (e.key === 'Escape') {
    e.preventDefault()
    if (state.selectedParty) { state.goBackToPartySearch() } else { onClose() }
    return
  }
  if (isTabSwitchShortcut(e, args)) {
    e.preventDefault()
    state.switchToMode(state.activeMode === 'account' ? 'party' : 'account')
    return
  }
  if (state.activeMode === 'account') { handleAccountKey(e, args); return }
  if (!state.selectedParty) { handlePartyStep1Key(e, args) } else { handlePartyStep2Key(e, args) }
}
